import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { useLang } from "../../context/LanguageContext";
import { img } from "../../lib/utils-app";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ImageUploader } from "../../components/ImageUploader";

const empty = { title: { en: "", es: "" }, excerpt: { en: "", es: "" }, content: { en: "", es: "" }, category: "market", author: "", image: "", published_at: "" };

export function ArticlesManager() {
  const { lang, L } = useLang();
  const es = lang === "es";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => { setLoading(true); const { data } = await api.get("/articles"); setItems(data.items); setLoading(false); };
  useEffect(() => { load(); }, []);
  const remove = async (a) => { if (!window.confirm(es ? "¿Eliminar artículo?" : "Delete article?")) return; await api.delete(`/articles/${a.id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-serif text-4xl font-light">{es ? "Blog" : "Blog"}</h1><p className="text-muted-foreground mt-1">{items.length}</p></div>
        <button onClick={() => { setEditing({ ...empty }); setOpen(true); }} className="flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground label-caps hover:brightness-110" data-testid="add-article-btn"><Plus size={16} /> {es ? "Nuevo artículo" : "New article"}</button>
      </div>
      {loading ? <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-primary" /></div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="articles-grid">
          {items.map((a) => (
            <div key={a.id} className="border border-border overflow-hidden" data-testid={`article-card-${a.id}`}>
              <div className="aspect-[16/9] overflow-hidden"><img src={img(a.image)} alt="" className="w-full h-full object-cover" /></div>
              <div className="p-3 flex items-center gap-2">
                <div className="flex-1 min-w-0"><p className="font-medium truncate">{L(a.title)}</p><p className="text-xs text-muted-foreground truncate">{a.category} · {a.author}</p></div>
                <button onClick={() => { setEditing({ ...empty, ...a, title: a.title || { en: "", es: "" }, excerpt: a.excerpt || { en: "", es: "" }, content: a.content || { en: "", es: "" } }); setOpen(true); }} className="p-2 text-muted-foreground hover:text-primary" data-testid={`edit-article-${a.id}`}><Pencil size={15} /></button>
                <button onClick={() => remove(a)} className="p-2 text-muted-foreground hover:text-destructive" data-testid={`delete-article-${a.id}`}><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <ArticleForm open={open} setOpen={setOpen} editing={editing} onSaved={load} lang={lang} />
    </div>
  );
}

function ArticleForm({ open, setOpen, editing, onSaved, lang }) {
  const es = lang === "es";
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (editing) setForm(editing); }, [editing]);
  if (!editing) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setN = (p, k, v) => setForm((f) => ({ ...f, [p]: { ...f[p], [k]: v } }));
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { if (form.id) await api.put(`/articles/id/${form.id}`, form); else await api.post("/articles", form); toast.success(es ? "Guardado" : "Saved"); setOpen(false); onSaved(); }
    catch { toast.error("Error"); } finally { setSaving(false); }
  };
  const inp = "w-full mt-1.5 bg-background border border-border focus:border-primary px-3 py-2.5 text-sm outline-none";
  const lbl = "label-caps text-muted-foreground text-[0.6rem]";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{form.id ? (es ? "Editar artículo" : "Edit article") : (es ? "Nuevo artículo" : "New article")}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4" data-testid="article-form">
          <div><label className={lbl}>{es ? "Imagen de portada" : "Cover image"}</label><div className="mt-1.5"><ImageUploader single aspect={16 / 9} value={form.image ? [form.image] : []} onChange={(a) => set("image", a[0] || "")} lang={lang} /></div></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>{es ? "Título (EN)" : "Title (EN)"}</label><input required value={form.title.en} onChange={(e) => setN("title", "en", e.target.value)} className={inp} data-testid="article-title-en" /></div>
            <div><label className={lbl}>{es ? "Título (ES)" : "Title (ES)"}</label><input value={form.title.es} onChange={(e) => setN("title", "es", e.target.value)} className={inp} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>{es ? "Categoría" : "Category"}</label><input value={form.category} onChange={(e) => set("category", e.target.value)} className={inp} placeholder="market / buying / selling / renting" /></div>
            <div><label className={lbl}>{es ? "Autor" : "Author"}</label><input value={form.author} onChange={(e) => set("author", e.target.value)} className={inp} /></div>
          </div>
          <div><label className={lbl}>{es ? "Extracto (EN)" : "Excerpt (EN)"}</label><textarea rows={2} value={form.excerpt.en} onChange={(e) => setN("excerpt", "en", e.target.value)} className={inp + " resize-none"} /></div>
          <div><label className={lbl}>{es ? "Extracto (ES)" : "Excerpt (ES)"}</label><textarea rows={2} value={form.excerpt.es} onChange={(e) => setN("excerpt", "es", e.target.value)} className={inp + " resize-none"} /></div>
          <div><label className={lbl}>{es ? "Contenido (EN)" : "Content (EN)"}</label><textarea rows={4} value={form.content.en} onChange={(e) => setN("content", "en", e.target.value)} className={inp + " resize-none"} /></div>
          <div><label className={lbl}>{es ? "Contenido (ES)" : "Content (ES)"}</label><textarea rows={4} value={form.content.es} onChange={(e) => setN("content", "es", e.target.value)} className={inp + " resize-none"} /></div>
          <button disabled={saving} className="w-full py-3 bg-primary text-primary-foreground label-caps disabled:opacity-50" data-testid="article-save">{saving ? "…" : (es ? "Guardar" : "Save")}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
